import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@ludus/ui'

/**
 * One breadcrumb segment. `to` makes it a router link; the final segment is
 * usually link-less (the current page). `mono` renders a non-text identifier
 * (e.g. a short lead id fallback) in monospace.
 */
export interface BreadcrumbItem {
  label: string
  to?: string
  mono?: boolean
}

/**
 * Shared breadcrumb used by the pipeline kanban header and the lead-detail
 * page. Lives in console `shared/` (not `packages/ui`) because it depends on
 * react-router's `<Link>`, and `packages/ui` forbids router imports — see
 * `apps/console/CLAUDE.md`. Two consumers across two features made the
 * inline-markup drift a real risk, so the markup lives in one place.
 */
export function Breadcrumb({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('text-[11px] font-semibold uppercase tracking-[0.12em] text-muted', className)}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => {
          const isLast = i === items.length - 1
          return (
            <Fragment key={`${item.label}-${i}`}>
              {i > 0 && (
                <li aria-hidden="true">
                  <ChevronRight className="h-3 w-3 text-muted/50" />
                </li>
              )}
              <li
                aria-current={isLast ? 'page' : undefined}
                className={cn(
                  item.mono && 'font-mono normal-case tracking-normal text-foreground/60',
                  // Non-mono link segments take their colour from the inner
                  // <Link> below; only the final (link-less) segment needs one.
                  !item.mono && isLast && 'text-foreground',
                )}
              >
                {item.to ? (
                  <Link to={item.to} className="text-muted transition-colors hover:text-foreground">
                    {item.label}
                  </Link>
                ) : (
                  item.label
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
